/**
 * Fan-paneel in de chat: de tabbladen Transactions en History zoals op het
 * oude dashboard (15-10-2026, Jordy).
 *
 * Gemeten aan de oude bundel (admin-dashboard/dist/assets/index-T5gddIQs.js,
 * 20-08): "Fan info & insights"-chips, "Spent on", "Tips vs PPV" met "Media
 * unlocks", "Latest transactions", "Unlock history", "Content history" en het
 * Resend-venster. Overgenomen uit tak chat/fanpaneel (08-10, niet gemerged),
 * zonder de backendwijzigingen van die tak: alles hier leest velden die de
 * server al meestuurt (fan_spend_by_category, fan_spend_mix,
 * fan_recent_transactions, fan_content_sends).
 *
 * Geen React: de tests voeren dit uit met node (esbuild).
 */
import { credits, eur } from './format'
import { FC_MAX_CREDITS, PPV_MIN_F2F_EUR, PPV_MIN_FC_CREDITS, isF2f, prijsAlsGetal } from './massPrijs'

// ── Abonnement en uitgaven ─────────────────────────────────────────────────

export interface Bedrag { amount: number; count: number }
export interface FanGeld {
  fan_total_spend?: number | null
  fan_subscription_status?: string | null
  fan_spend_by_category?: Record<string, Bedrag> | null
  fan_spend_breakdown?: Record<string, Bedrag> | null
  fan_spend_mix?: Record<string, unknown> | null
}

export function abonnementTekst(status: unknown): string {
  const s = String(status ?? 'unknown').toLowerCase()
  return s === 'active' ? 'Active' : s === 'recorded' ? 'Previously recorded' : 'Unknown'
}

/** "12 days" / "3 months" sinds een datum; "Unknown" zonder datum. */
export function duurSinds(v: unknown, nu: Date = new Date()): string {
  if (!v) return 'Unknown'
  const t = new Date(String(v))
  if (Number.isNaN(t.getTime())) return 'Unknown'
  const dagen = Math.max(0, Math.floor((nu.getTime() - t.getTime()) / 864e5))
  if (dagen < 31) return `${dagen} day${dagen === 1 ? '' : 's'}`
  const m = Math.floor(dagen / 30.44)
  return `${m} month${m === 1 ? '' : 's'}`
}

function bedragUit(x: unknown): Bedrag {
  const b = (x ?? {}) as Partial<Bedrag>
  return { amount: Number(b.amount || 0), count: Number(b.count || 0) }
}

/** Fooien: de gecanoniseerde categorie `tip`, anders `fan_spend_mix.tips`,
 *  anders de ruwe spellingen. null = er is niets over fooien bekend.
 *  15-10-2026: `fan_spend_mix.tips` staat er altijd (ook met 0); alleen een
 *  bedrag telt, anders zou "€0,00 tips" staan waar oud "No tips recorded" zei. */
export function fooien(c: FanGeld | null | undefined): number | null {
  const cat = c?.fan_spend_by_category?.tip
  if (cat) return Number(cat.amount || 0)
  const mix = bedragUit(c?.fan_spend_mix?.tips)
  if (mix.amount > 0) return mix.amount
  const ruw = c?.fan_spend_breakdown ?? {}
  const delen = ['tip', 'tips', 'fooi'].map((k) => Number(ruw[k]?.amount || 0))
  return delen.some((x) => x !== 0) ? delen.reduce((a, b) => a + b, 0) : null
}

const SPENT_ON: { label: string; categories: string[] }[] = [
  { label: 'Tips', categories: ['tip'] },
  { label: 'Chat unlocks', categories: ['chat_attachment', 'dm_unlock', 'video_unlock', 'paid_message'] },
  { label: 'Subscriptions', categories: ['subscription', 'renew'] },
  { label: 'Paid posts', categories: ['post'] },
]

export interface SpentRegel { label: string; amount: number; count: number; pct: number | null }

/** "Spent on": per soort het bedrag, het aantal en het aandeel. */
export function uitgegevenAan(c: FanGeld | null | undefined): SpentRegel[] {
  const cat = c?.fan_spend_by_category ?? {}
  const bekend = new Set(SPENT_ON.flatMap((s) => s.categories))
  const regels: { label: string; amount: number; count: number }[] = []
  for (const s of SPENT_ON) {
    if (!s.categories.some((k) => cat[k])) continue
    regels.push({
      label: s.label,
      amount: s.categories.reduce((a, k) => a + Number(cat[k]?.amount || 0), 0),
      count: s.categories.reduce((a, k) => a + Number(cat[k]?.count || 0), 0),
    })
  }
  const overig = Object.entries(cat).filter(([k]) => !bekend.has(k))
  if (overig.length) {
    regels.push({
      label: 'Other purchases',
      amount: overig.reduce((a, [, v]) => a + Number(v?.amount || 0), 0),
      count: overig.reduce((a, [, v]) => a + Number(v?.count || 0), 0),
    })
  }
  if (!regels.length) return []
  const som = regels.reduce((a, r) => a + r.amount, 0)
  const totaal = Number(c?.fan_total_spend || 0)
  const basis = som > 0.005 ? som : totaal
  const uit: SpentRegel[] = regels.map((r) => ({
    ...r, pct: basis > 0.005 ? Math.round((r.amount / basis) * 100) : null,
  }))
  if (totaal - som > 0.005) {
    uit.push({ label: 'Not itemised', amount: Math.round((totaal - som) * 100) / 100, count: 0, pct: null })
  }
  return uit
}

export interface TipsPpv {
  tips: Bedrag & { pct: number }
  ppv: Bedrag & { pct: number }
  subscriptions: Bedrag
  typering: string | null
  media: { label: string; amount: number; count: number }[]
}

/** "Tips vs PPV" uit `fan_spend_mix`; null als er niets is uitgegeven. */
export function tipsTegenPpv(c: FanGeld | null | undefined): TipsPpv | null {
  const m = c?.fan_spend_mix
  if (!m || typeof m !== 'object') return null
  const tips = bedragUit(m.tips)
  const ppv = bedragUit(m.ppv)
  const subs = bedragUit(m.subscriptions)
  const other = bedragUit(m.other)
  const som = tips.amount + ppv.amount + subs.amount + other.amount
  if (som <= 0.005) return null
  const media = (m.media ?? {}) as Record<string, unknown>
  const soorten: [string, string][] = [
    ['video', 'Videos'], ['photo', 'Photos'],
    ['chat_media', 'Chat media (photo/video)'], ['message', 'Paid text messages'],
  ]
  const n = tips.amount
  const r = ppv.amount
  const typering = n > r * 1.15 ? 'Tips more than PPV'
    : r > n * 1.15 ? 'PPV more than tips'
    : n > 0.005 && r > 0.005 ? 'Tips and PPV both strong'
    : n > 0.005 ? 'Mostly tips'
    : r > 0.005 ? 'Mostly PPV / unlocks' : null
  return {
    tips: { ...tips, pct: Math.round((n / som) * 100) },
    ppv: { ...ppv, pct: Math.round((r / som) * 100) },
    subscriptions: subs,
    typering,
    media: soorten
      .map(([k, label]) => ({ label, ...bedragUit(media[k]) }))
      .filter((x) => x.amount > 0.005 || x.count > 0),
  }
}

export function mediaSoortTekst(kind: unknown): string {
  const k = String(kind ?? '')
  return k === 'video' ? 'Video' : k === 'photo' ? 'Photo' : k === 'chat_media' ? 'Chat media'
    : k === 'message' ? 'Message' : '—'
}

// ── History ────────────────────────────────────────────────────────────────

export interface Verzending {
  message_id: number
  bundle_id?: string | null
  bundle_item_count?: number
  content_item_id?: number | null
  content_item_ids?: number[]
  media_url?: string | null
  media_type?: string | null
  is_paid?: boolean
  price?: number
  is_unlocked?: boolean
  unlocked_at?: string | null
  created_at?: string | null
  caption?: string | null
  can_resend?: boolean
  delivered?: boolean | null
  delivery_error?: string | null
  can_retract_locally?: boolean
}

export interface Transactie {
  at?: string | null
  amount?: number
  event_type?: string
  category?: string
  label?: string
  media_kind?: string | null
}

/** Een prijs in de munt van het platform: FC credits, F2F euro's. */
export function prijsTekst(prijs: unknown, platform: unknown): string {
  return isF2f(String(platform ?? '')) ? eur(prijs) : credits(prijs)
}

/** "Paid · locked · 800 credits" / "Paid · unlocked · €5,00" / "Free". */
export function inhoudLabel(v: Verzending, platform: unknown): string {
  if (!v.is_paid) return 'Free'
  return `Paid · ${v.is_unlocked ? 'unlocked' : 'locked'} · ${prijsTekst(v.price, platform)}`
}

export function isUnlockTx(t: Transactie): boolean {
  const s = String(t.category || t.event_type || '').toLowerCase()
  return s.includes('unlock') || s.includes('attachment') || s === 'paid_message'
}

export interface UnlockRegels { berichten: Verzending[]; transacties: Transactie[] }

/** De unlock-historie: gekochte berichten uit de content-historie, plus de
 *  unlock-transacties (hoogstens acht). */
export function unlockRegels(sends: Verzending[] | null | undefined,
                             tx: Transactie[] | null | undefined): UnlockRegels {
  return {
    berichten: (sends ?? []).filter((s) => s.is_unlocked && Number(s.message_id || 0) > 0),
    transacties: (tx ?? []).filter(isUnlockTx).slice(0, 8),
  }
}

// ── Resend ─────────────────────────────────────────────────────────────────

/** Een tijdelijke [video]/[paid image]-aanduiding is geen tekst om mee te sturen. */
export function schoneTekst(t: unknown): string {
  const s = String(t ?? '').trim()
  return /^\[(paid\s+)?(video|image|audio|media)\b/i.test(s) ? '' : s
}

export function inhoudIds(v: Verzending): number[] {
  const lijst = Array.isArray(v.content_item_ids) && v.content_item_ids.length
    ? v.content_item_ids
    : [Number(v.content_item_id || 0)]
  return lijst.map(Number).filter((n) => n > 0)
}

/** Wat er mis is met de nieuwe prijs; null = in orde. Leeg of 0 = gratis.
 *  Zelfde grenzen als de server voor privéberichten (500 credits / €5). */
export function resendPrijsFout(platform: unknown, prijs: string): string | null {
  const tekst = String(prijs ?? '').trim()
  if (!tekst) return null
  const n = prijsAlsGetal(tekst)
  if (!Number.isFinite(n) || n < 0) return 'Enter a price, or 0 for free.'
  if (n === 0) return null
  if (isF2f(String(platform ?? ''))) {
    return n < PPV_MIN_F2F_EUR ? `Paid content on F2F: at least €${PPV_MIN_F2F_EUR}.` : null
  }
  if (n !== Math.trunc(n)) return 'FanCentro prices are whole credits (no decimals).'
  if (n > FC_MAX_CREDITS) return `FanCentro: at most ${FC_MAX_CREDITS.toLocaleString('en-US')} credits.`
  if (n < PPV_MIN_FC_CREDITS) return `Paid content on FanCentro: at least ${PPV_MIN_FC_CREDITS} credits.`
  return null
}

export interface ResendApi {
  post: (path: string, body?: unknown) => Promise<unknown>
  del: (path: string) => Promise<unknown>
}

export interface ResendKeuze {
  conversationId: number
  platform: string
  item: Verzending
  tekst: string
  prijs: string
  eerstVerwijderen: boolean
  alsGetypt: boolean
  requestId?: string
}

/**
 * Opnieuw versturen zoals oud: eventueel eerst het oude bericht weghalen
 * (alleen als het nog niet is gekocht), dan dezelfde bibliotheekitems via
 * `send-library-content` met de nieuwe tekst en prijs.
 */
export async function verstuurOpnieuw(api: ResendApi, k: ResendKeuze): Promise<unknown> {
  const ids = inhoudIds(k.item)
  if (!ids.length) throw new Error('This send has no library id — open the content library and re-pick the media.')
  const fout = resendPrijsFout(k.platform, k.prijs)
  if (fout) throw new Error(fout)
  const n = prijsAlsGetal(k.prijs)
  const betaald = Number.isFinite(n) && n > 0
  if (k.eerstVerwijderen && !k.item.is_unlocked && k.item.message_id) {
    await api.del(`/api/admin/conversations/${k.conversationId}/messages/${k.item.message_id}`)
  }
  const rid = k.requestId
    || `resend-${k.conversationId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  return api.post(`/api/admin/conversations/${k.conversationId}/send-library-content`, {
    content_item_ids: ids,
    caption: schoneTekst(k.tekst),
    is_ppv: betaald,
    ppv_price: betaald ? n : 0,
    client_request_id: rid,
    // 15-10-2026 (Jordy): op F2F beslist de server (altijd vertalen volgens de fan).
    ...(isF2f(String(k.platform ?? '')) ? {} : { translation_enabled: !k.alsGetypt }),
  })
}
